import React, { Suspense, useCallback, useEffect, useState } from 'react';
import { ApolloProvider } from '@apollo/client';
import { Helmet } from 'react-helmet';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';

import makeApolloClient from './apollo';
import { resolveAuthenticationState } from './auth/resolveAuthenticationState';
import { UserContextProvider } from './components/Dashboard/store/UserContext';
import { LegacyLoginPage } from './components/Login';
import { phoneOnboardingApi } from './components/PhoneOnboarding/phoneOnboardingApi';
import { PhoneOnboardingPage } from './components/PhoneOnboarding/PhoneOnboardingPage';
import { HasuraAuthUIWrapper } from './components/Signup/HasuraAuthUIWrapper';
import { useAuthModes } from './components/Signup/useAuthModes';
import { authRedirect } from './utils/auth';
import { authEndpoints, EE_MODE, GRAPHQL_URL, WS_URL } from './utils/constants';
import { heapIdentify } from './utils/heapAnalytics';
import { isLoggedIn, parseQueryString } from './utils/helpers';
import { lazyLoadSafely } from './utils/loadErrorHander';
import NetworkLoader from './utils/NetworkLoader';
import { updateOpenReplayUserId } from './utils/openReplay';
import { trackUser } from './utils/posthog';
import requestAuthServer from './utils/requestAuthServer';
import {
  authPaths,
  getRouteHelmetTitle,
  isUnProtectedRoute,
  paths,
  unProtectedRoutes,
} from './utils/routeUtils';
import { segmentEvents, segmentTrack } from './utils/segment';
import { sentryIdentify } from './utils/sentry';
import { UserInfo } from './utils/types';

const App = lazyLoadSafely(() => import('./App'));
const ResetPassword = lazyLoadSafely(
  () => import('./components/Login/ResetPassword')
);
const Confirm = lazyLoadSafely(() => import('./components/Callbacks/Confirm'));
const Consent = lazyLoadSafely(() => import('./components/Callbacks/Consent'));
const HydraError = lazyLoadSafely(
  () => import('./components/HydraError/HydraError')
);
const VercelIntegrationLogin = lazyLoadSafely(
  () => import('./components/VercelIntegration/Login')
);
// commenting out SSO temporarily untill related issues are fixed
// const VercelSSO = lazyLoadSafely(
//   import('./components/VercelIntegration/SSO').catch(importErrorHandler)
// );
const VercelSignup = lazyLoadSafely(
  () => import('./components/VercelIntegration/Signup')
);
const VercelEmailLogin = lazyLoadSafely(
  () => import('./components/VercelIntegration/EmailLogin')
);
const VercelResetPassword = lazyLoadSafely(
  () => import('./components/VercelIntegration/ResetPassword')
);

// This is the function that checks the logged-in status of the user.
// We accomplish this by making a call to the user info API.
// `requestAuthServer` here is simply a wrapper around fetch that calls the auth server. It throws
// errors on receiving unexpected HTTP status code. (Refer to the function's
// documentation for more info.)
//
// If user is logged in we setup the Apollo client and gmetrics tracking.
// Otherwise, we redirect to the login/signup page.
const checkUserLogin = () => {
  const options = {
    credentials: 'include',
    method: 'GET',
    headers: {
      'content-type': 'application/json',
    },
  };

  return requestAuthServer(authEndpoints.userInfo, options);
};

// TODO: Remove any from the following type
export type AuthState = {
  isAuthenticated: Boolean;
  hasLoadedOnce: Boolean;
  fetchUserInProcess: Boolean;
  userInfo: UserInfo | null;
  loginRedirectLocation: string | null;
  isRedirecting: Boolean;
  authenticationState:
    | 'authenticated'
    | 'phone_verification_required'
    | 'unauthenticated';
  phoneOnboardingExpiresAt: string | null;
};

const defaultState: AuthState = {
  isAuthenticated: false,
  hasLoadedOnce: false,
  fetchUserInProcess: false,
  userInfo: null,
  loginRedirectLocation: null,
  isRedirecting: false,
  authenticationState: 'unauthenticated',
  phoneOnboardingExpiresAt: null,
};

const AuthenticatedRoutes: React.FC = () => {
  const client = React.useMemo(() => {
    return makeApolloClient(GRAPHQL_URL, WS_URL);
  }, []);

  return (
    <ApolloProvider client={client}>
      <Suspense fallback={<NetworkLoader />}>
        <UserContextProvider>
          <App data-openreplay-hidden />
        </UserContextProvider>
      </Suspense>
    </ApolloProvider>
  );
};

const AuthCheck: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [authState, setAuthState] = useState<AuthState>(defaultState);

  const { pathname, search } = location;

  const navigateToLogin = useCallback(() => {
    const parsedQueryString = parseQueryString(search);
    const parseLength = Object.keys(parsedQueryString).length;

    if (!isUnProtectedRoute(pathname)) {
      if (parseLength === 0 || !('redirect_url' in parsedQueryString)) {
        navigate({
          pathname: paths.signup(),
          search: `?redirect_url=${encodeURIComponent(
            `${pathname}${search ? `${search}` : ''}`
          )}`,
        });
      } else {
        navigate({
          pathname: paths.signup(),
        });
      }
    }
  }, [navigate, pathname, search]);

  const leavePhoneOnboarding = useCallback(() => {
    setAuthState(prevState => ({
      ...prevState,
      authenticationState: 'unauthenticated',
      phoneOnboardingExpiresAt: null,
      isAuthenticated: false,
      userInfo: null,
    }));
    navigateToLogin();
  }, [navigateToLogin]);

  // Abandoning must also discard the continuation and its cookie. Local state
  // alone leaves a live onboarding token in the browser, so the next page load
  // probes it and lands the user right back on the verification screen.
  //
  // Only the explicit abandon cancels. Expiration is automatic and its
  // continuation is already dead, and cancelling clears the cookie
  // unconditionally, so a timer firing in a stale tab would otherwise evict the
  // cookie a concurrent login had just issued in another one.
  const abandonPhoneOnboarding = useCallback(() => {
    void phoneOnboardingApi.cancel().then(leavePhoneOnboarding);
  }, [leavePhoneOnboarding]);

  const retriveUser = useCallback(() => {
    setAuthState(prevState => ({
      ...prevState,
      fetchUserInProcess: true,
    }));

    checkUserLogin()
      .then(async resp => {
        const authenticationResolution = await resolveAuthenticationState({
          userInfo: resp,
          eeMode: EE_MODE,
          getOnboardingStatus: phoneOnboardingApi.getStatus,
        });
        const authenticationState = authenticationResolution.state;

        if (authenticationState === 'authenticated') {
          const userId = resp['X-Hasura-User-Id'];
          segmentTrack(segmentEvents.cloud, {
            email: resp['X-Hasura-User-Email'],
            userId,
            label: 'User Authenticated',
          });
          trackUser(userId, resp['X-Hasura-User-Email']);
          updateOpenReplayUserId(resp['X-Hasura-User-Email'] || userId);
          heapIdentify(userId);
          sentryIdentify(userId);
          setAuthState(prevState => ({
            ...prevState,
            isAuthenticated: true,
            fetchUserInProcess: false,
            userInfo: resp,
            hasLoadedOnce: true,
            authenticationState,
            phoneOnboardingExpiresAt: null,
          }));
          return;
        }

        if (authenticationState === 'unauthenticated') {
          navigateToLogin();
        }

        setAuthState(prevState => ({
          ...prevState,
          isAuthenticated: false,
          isRedirecting: false,
          fetchUserInProcess: false,
          hasLoadedOnce: true,
          userInfo: null,
          authenticationState,
          phoneOnboardingExpiresAt:
            authenticationResolution.state === 'phone_verification_required'
              ? authenticationResolution.expiresAt
              : null,
        }));
      })
      .catch(() => {
        navigateToLogin();
        setAuthState(prevState => ({
          ...prevState,
          isAuthenticated: false,
          isRedirecting: false,
          fetchUserInProcess: false,
          hasLoadedOnce: true,
          userInfo: null,
          authenticationState: 'unauthenticated',
          phoneOnboardingExpiresAt: null,
        }));
      });
  }, [navigateToLogin]);

  const afterLogin = React.useCallback(
    ({ loginRedirectLocation }: { loginRedirectLocation: string }) => {
      setAuthState(prevState => ({
        ...prevState,
        loginRedirectLocation,
        isRedirecting: true,
      }));
      retriveUser();
    },
    [retriveUser]
  );

  useEffect(() => {
    retriveUser();
  }, []);

  useEffect(() => {
    if (authState.isAuthenticated) {
      const redirectCallback = () =>
        setAuthState(s => ({ ...s, isRedirecting: false }));
      authRedirect(
        navigate,
        location,
        authState.loginRedirectLocation,
        redirectCallback
      );
    }
  }, [authState.isAuthenticated]);

  const authModes = useAuthModes();

  if (authState.authenticationState === 'phone_verification_required') {
    return (
      <PhoneOnboardingPage
        expiresAt={authState.phoneOnboardingExpiresAt}
        onAbandon={abandonPhoneOnboarding}
        onSessionExpired={leavePhoneOnboarding}
        onVerified={retriveUser}
      />
    );
  }

  if (
    !authState.hasLoadedOnce ||
    authState.fetchUserInProcess ||
    authState.isRedirecting
  ) {
    return <NetworkLoader />;
  }

  return (
    <div key="auth-check-wrapper">
      <Helmet title={getRouteHelmetTitle(location)} />
      <Suspense fallback={<NetworkLoader />}>
        <Routes>
          <Route
            key="reset-password"
            path={paths.recover()}
            element={<ResetPassword />}
          />
          <Route
            key="confirm-auth"
            path={paths.callbacks().confirm()}
            element={<Confirm />}
          />
          <Route
            key="consent-auth"
            path={paths.callbacks().consent()}
            element={<Consent />}
          />
          <Route path={paths.oauthError()} element={<HydraError />} />
          <Route
            key="vercel-login"
            path={paths.vercel().login()}
            element={
              authState.isAuthenticated &&
              new URLSearchParams(window.location.search).get('next') !==
                null ? (
                <Navigate
                  to={`${paths.vercel().setup()}${window.location.search}`}
                />
              ) : (
                <VercelIntegrationLogin />
              )
            }
          />
          <Route
            key="vercel-email-signup"
            path={paths.vercel().signup()}
            element={
              !authState.isAuthenticated ? (
                <VercelSignup />
              ) : (
                <Navigate
                  to={`${paths.vercel().setup()}${window.location.search}`}
                />
              )
            }
          />
          <Route
            key="vercel-email-login"
            path={paths.vercel().emailLogin()}
            element={
              !authState.isAuthenticated ? (
                <VercelEmailLogin />
              ) : (
                <Navigate
                  to={`${paths.vercel().setup()}${window.location.search}`}
                />
              )
            }
          />
          <Route
            key="vercel-reset-password"
            path={paths.vercel().resetPassword()}
            element={
              !authState.isAuthenticated ? (
                <VercelResetPassword />
              ) : (
                <Navigate
                  to={`${paths.vercel().setup()}${window.location.search}`}
                />
              )
            }
          />
          {!isLoggedIn(authState.userInfo) ? (
            <>
              <Route
                path={paths.login()}
                element={
                  <Navigate
                    to={{ pathname: authPaths.default(), search }}
                    replace
                  />
                }
              />
              {Object.values(authPaths).map(path => (
                <Route
                  key={`v3-unprotected-${path()}`}
                  path={path()}
                  element={
                    <HasuraAuthUIWrapper
                      authModes={authModes.modes}
                      afterLogin={afterLogin}
                    />
                  }
                />
              ))}
              {unProtectedRoutes.v2
                // /login is handled above
                .filter(p => p !== paths.login())
                .map(path => (
                  <Route
                    key={`v2-unprotected-${path}`}
                    path={path}
                    element={
                      <LegacyLoginPage
                        {...authState}
                        loginCallback={afterLogin}
                        isLoggedIn={isLoggedIn(authState.userInfo)}
                      />
                    }
                  />
                ))}
            </>
          ) : (
            <Route
              key="rest-app"
              path={paths.rootWithChildren()}
              element={<AuthenticatedRoutes />}
            />
          )}
        </Routes>
      </Suspense>
    </div>
  );
};

export default AuthCheck;
